import React from 'react';
import {
    procedureStatusOf, approvedTotalOf, acceptedTotalOf, formatInr, toothLabel, isTerminalStatus,
} from './reimbursementStatus';
import { SECTION_HEADING } from './FormShell';

const PILL = 'inline-flex items-center text-[11px] font-bold px-2 py-0.5 rounded-full border';

// The procedures a ToothLens reviewer tabulated from the member's prescription, in the reviewer's
// order, each with its own current status — Approved, Rejected or Pending — whatever the others'
// (procedureStatusOf). An approved one shows its amount and where its proof stands; a rejected one
// its reason.
const ProcedureGroups = ({ reimbursement }) => {
    const procedures = reimbursement?.procedures || [];
    if (!procedures.length) return null;
    const anyDecided = procedures.some((p) => p.decision && p.decision !== 'pending');
    const accepted = acceptedTotalOf(reimbursement);

    return (
        <section className="mt-4" data-testid="recommended-procedures">
            <p className={`${SECTION_HEADING} mb-2`}>Recommended procedures</p>
            <div className="border border-portal-border rounded-xl bg-white divide-y divide-portal-border">
                <ul className="divide-y divide-portal-border">
                    {procedures.map((procedure) => {
                        const status = procedureStatusOf(procedure, reimbursement);
                        return (
                            <li key={procedure._id} className="px-3 py-2.5 text-xs" data-testid="recommended-procedure">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <p className="font-semibold text-portal-ink">{procedure.treatment}</p>
                                        <p className="text-portal-sub">
                                            {[toothLabel(procedure.tooth), procedure.clinical_finding, procedure.priority].filter(Boolean).join(' · ')}
                                        </p>
                                    </div>
                                    <div className="flex-shrink-0 flex flex-col items-end gap-1">
                                        <span className={`${PILL} ${status.tone}`} data-testid="recommended-procedure-status">{status.label}</span>
                                        {procedure.decision === 'approved' && (
                                            <span className="font-semibold text-portal-ink">{formatInr(procedure.approved_amount)}</span>
                                        )}
                                    </div>
                                </div>
                                {procedure.decision === 'rejected' && procedure.decision_note && (
                                    <p className="text-portal-sub mt-1">Reason: {procedure.decision_note}</p>
                                )}
                                {status.proof && (
                                    <p className="mt-1.5">
                                        <span className={`${PILL} ${status.proof.tone}`} data-testid="recommended-procedure-proof">{status.proof.label}</span>
                                    </p>
                                )}
                            </li>
                        );
                    })}
                </ul>
                {anyDecided && (
                    <dl className="px-3 py-2.5 text-xs space-y-1 bg-portal-card rounded-b-xl">
                        <div className="flex justify-between gap-3" data-testid="approved-total">
                            <dt className="text-portal-sub">Approved for reimbursement</dt>
                            <dd className="font-bold text-portal-ink">{formatInr(approvedTotalOf(reimbursement))}</dd>
                        </div>
                        {accepted > 0 && (
                            <div className="flex justify-between gap-3" data-testid="accepted-total">
                                <dt className="text-portal-sub">{isTerminalStatus(reimbursement.reimbursement_status) ? 'Accepted for reimbursement' : 'Accepted so far'}</dt>
                                <dd className="font-bold text-portal-green-ink">{formatInr(accepted)}</dd>
                            </div>
                        )}
                    </dl>
                )}
            </div>
        </section>
    );
};

export default ProcedureGroups;
